import React, { useCallback, useEffect, useState } from "react";
import {
  AppointmentCardControlled,
  NHEAppointmentStatusEnum,
} from "./card-controlled";
import {
  ApplicationManageData,
  JobScheduleDetails,
  NheDetails,
  SdaInfo,
} from "../../utility/application-data";
import {
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  BACKGROUND_CHECK_STATUS_SF_ENDPOINT,
  buildUrl,
  DRUG_TEST_STATUS_ENDPOINT,
} from "../../config/urls";
import { getCandidateData } from "../../utility/candidate-data";
import { getCaasInfo } from "../../service/get-caas-info";
import { fetchWithTimeout } from "../../helpers/fetcher";
import { ContingencyResponse } from "../screening-results/card-uncontrolled";
import { I9Response } from "../i9-task/i9-controlled";
import { getSettings, Stage } from "../../helpers/get-settings";
import {
  isCustomerService,
  isTaskEnabledByAppConfig,
} from "../../utility/app-config-helper";
import {
  BGC_CHECK_IN_SCREENING_TASK_ID,
  DOCUMENT_TASK_ID,
  I9_TASK_ID,
} from "../../config/appConfigConstants";

interface AppointmentCardUncontrolledProps {
  sdaAccessor: () => Promise<SdaInfo | undefined>;
  nheAccessor: () => Promise<NheDetails | undefined>;
  jcAccessor: () => Promise<JobScheduleDetails | undefined>;
  applicationManageAccessor: () => Promise<ApplicationManageData | undefined>;
  trackNHEDetailsClick: () => void;
  trackStartDateDetailsClick: () => void;
  trackSupportDocClick: () => void;
  cssURLWithoutScheduleId?: string;
  appointmentSelfServiceURL: string;
  applicationId: string;
  dtResponse?: ContingencyResponse;
  bgcResponse?: ContingencyResponse;
  i9Response?: I9Response;
}

export const AppointmentCardUncontrolled = ({
  jcAccessor,
  sdaAccessor,
  nheAccessor,
  applicationManageAccessor,
  cssURLWithoutScheduleId,
  trackNHEDetailsClick,
  trackStartDateDetailsClick,
  trackSupportDocClick,
  applicationId,
  appointmentSelfServiceURL,
}: AppointmentCardUncontrolledProps) => {
  const [sdaDetails, setSdaDetails] = useState<SdaInfo | undefined>();
  const [nheDetails, setNheDetails] = useState<NheDetails | undefined>();
  const [jcDetails, setJcDetails] = useState<JobScheduleDetails | undefined>();
  const [displayNHE, setDisplayNHE] = useState<boolean>(false);
  const [displayScreening, setDisplayScreening] = useState<boolean>(false);
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [
    eligibleForAppointmentRescheduling,
    setEligibleForAppointmentRescheduling,
  ] = useState<boolean>(false);
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [i9Response, setI9Response] = useState<I9Response>();
  const [bgcResponse, setBgcResponse] = useState<ContingencyResponse>();
  const [dtResponse, setDtResponse] = useState<ContingencyResponse>();
  const [stage, setStage] = useState<Stage>();

  const requestNheAndSda = useCallback(async () => {
    setLoading(true);
    try {
      const sdaDetails = await sdaAccessor();
      const nheDetails = await nheAccessor();
      const jcDetails = await jcAccessor();
      const applicationManageDetails = await applicationManageAccessor();

      const isEligibleToDisplayNhe = () => {
        return !(
          nheDetails?.inPast ||
          (applicationManageDetails?.applicationState &&
            nheDetails?.status !== NHEAppointmentStatusEnum.RESERVED) ||
          nheDetails?.status === NHEAppointmentStatusEnum.COMPLETED
        );
      };
      if (
        applicationManageDetails?.applicationState === "CONTINGENCIES_PENDING"
      ) {
        setDisplayScreening(true);
      }

      if (applicationManageDetails?.enableCSS) {
        setEligibleForCSS(true);
      }

      if (
        applicationManageDetails?.applicationState &&
        !nheDetails?.isStartTimeInPast
      ) {
        setEligibleForAppointmentRescheduling(true);
      }

      if (jcDetails?.error) {
        if (jcDetails?.error?.errorCode === "APPLICATION_DETAILS_NOT_FOUND") {
          setScheduleIdFound(false);
        }
      } else {
        setJcDetails(jcDetails);
      }

      if (sdaDetails?.errorCode) {
        setError(true);
      } else {
        setSdaDetails(sdaDetails);

        if (nheDetails && !nheDetails.errorStatusCode) {
          setDisplayNHE(isEligibleToDisplayNhe());
        }
        setNheDetails(nheDetails);
        setError(false);
        const { candidateId, sfCandidateId } = await getCandidateData(
          applicationId
        );

        if (!candidateId || !sfCandidateId) {
          setError(true);
        }

        const bgcURL = buildUrl(
          isCustomerService(applicationId)
            ? BACKGROUND_CHECK_STATUS_SF_ENDPOINT
            : BACKGROUND_CHECK_STATUS_ENDPOINT,
          {
            candidateId: candidateId,
            applicationId,
          }
        );

        const dtURL = buildUrl(DRUG_TEST_STATUS_ENDPOINT, {
          sfCandidateId: sfCandidateId,
          applicationId,
        });

        if (isTaskEnabledByAppConfig(I9_TASK_ID, applicationId)) {
          setI9Response(await getCaasInfo(candidateId));
        }

        if (
          isTaskEnabledByAppConfig(
            BGC_CHECK_IN_SCREENING_TASK_ID,
            applicationId
          )
        ) {
          setBgcResponse(await fetchWithTimeout(bgcURL));
        }

        if (isTaskEnabledByAppConfig(DOCUMENT_TASK_ID, applicationId)) {
          setDtResponse(await fetchWithTimeout(dtURL));
        }
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestNheAndSda();
    getSettings().then((settings) => setStage(settings.stage));
  }, []);

  return (
    <AppointmentCardControlled
      onViewNHEDetailsClick={trackNHEDetailsClick}
      onViewStartDateDetailsClick={trackStartDateDetailsClick}
      onViewListOfDocsClick={trackSupportDocClick}
      sdaDetails={sdaDetails}
      jcDetails={jcDetails}
      nheDetails={nheDetails}
      displayNHE={displayNHE}
      displayScreening={displayScreening}
      scheduleIdFound={scheduleIdFound}
      cssURLWithoutScheduleId={cssURLWithoutScheduleId}
      appointmentSelfServiceURL={appointmentSelfServiceURL}
      onRetry={requestNheAndSda}
      loading={loading}
      error={error}
      dtResponse={dtResponse}
      bgcResponse={bgcResponse}
      i9Response={i9Response}
      stage={stage}
      eligibleForCSS={eligibleForCSS}
      eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
    />
  );
};
